<template>
  <div>
    <RightSidePanel
      title="Chat"
      description="Directly chat with a candidate or teammate"
      @handle-close-panel="handleClosePanel"
    >
      <div v-for="message in contactMessages" :key="message.id" class="w-3/4 mx-auto">
        <div v-if="message.message_type == 'Customer'" class="chat chat-start">
          <div class="chat-bubble text-sm">
            <img v-if="message.telco_media_url" :src="message.telco_media_url" />
            {{ message.body }}
          </div>
          <div class="chat-footer opacity-50">
            <time class="text-xs opacity-50">{{ fromNow(message.created_at) }}</time>
          </div>
        </div>
        <div v-else class="chat chat-end">
          <div
            v-if="message.telco_message_status == 'undelivered' || message.telco_message_status == 'delivery_failed'"
            class="chat-image"
          >
            <div class="tooltip tooltip-left tooltip-error" :data-tip="message.telco_message_error">
              <vue-feather type="alert-circle" class="text-error cursor-pointer" />
            </div>
          </div>
          <div class="chat-bubble text-sm">{{ message.body }}</div>
          <div class="chat-footer opacity-50">
            <time class="text-xs opacity-50">{{ fromNow(message.created_at) }}</time>
          </div>
        </div>
      </div>

      <div class="w-3/4 mx-auto mb-4">
        <div class="mt-6 sm:mt-5 sm:pt-5">
          <div class="mt-1 sm:mt-0">
            <div class="max-w-lg grid rounded-md border border-gray-300 p-2">
              <div class="w-full flex justify-between items-center">
                <div class="col-span-7 flex text-indigo-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="cursor-pointer h-5 w-5 mr-2"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    @click="showEmoticons = !showEmoticons"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx="12" cy="12" r="9" />
                    <line x1="9" y1="9" x2="9.01" y2="9" />
                    <line x1="15" y1="9" x2="15.01" y2="9" />
                    <path d="M8 13a4 4 0 1 0 8 0m0 0h-8" />
                  </svg>
                </div>
                <div class="flex justify-end">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-indigo-600 col-span-1 cursor-pointer h-6 w-6"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    @click="message ? sendMessage() : null"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx="12" cy="12" r="9" />
                    <line x1="12" y1="8" x2="8" y2="12" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="16" y1="12" x2="12" y2="8" />
                  </svg>
                </div>
              </div>
              <div class="w-full">
                <TextAreaInput
                  v-if="!showEmoticons"
                  ref="messageInput"
                  :model-value="message"
                  class="focus:ring-indigo-100 focus:border-indigo-100 block w-full sm:text-sm border-transparent rounded-md -mt-2"
                  placeholder="Enter your message..."
                  @update:model-value="message = $event"
                />
                <div v-if="showEmoticons" class="flex justify-center">
                  <Picker
                    :data="index"
                    :style="{ maxHeight: '250px', width: '100%' }"
                    :set="'apple'"
                    :show-preview="false"
                    @select="
                      message = message + `${$event.native}`;
                      showEmoticons = false;
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-3">
          <SelectMenu
            label="Merge Tags"
            initial-value="Select a merge tag..."
            :options="mergeTags"
            @selected-option="tag => $refs.messageInput.insertTextAtCursor(tag)"
          />
          <SelectMenu
            ref="smsTemplateselectMenu"
            label="Use existing template"
            :initial-value="smsTemplates.find(t => t.id === selectedTemplateId) || 'Select an SMS template...'"
            :options="smsTemplates.map(t => t.name)"
            @selected-option="template => setSmsTemplate(template)"
          />
        </div>
        <div class="mt-4 bg-gray-100 rounded p-6 text-sm flex justify-between">
          <div class="mt-2 text-sm leading-5 text-gray-900">Looking to create more SMS templates?</div>
          <router-link to="/settings/templates/sms" target="_blank">
            <TButton label="Template Library" color="white" />
          </router-link>
        </div>
      </div>
    </RightSidePanel>
  </div>
</template>

<script>
import RightSidePanel from './RightSidePanel.vue';
import 'emoji-mart-vue-fast/css/emoji-mart.css';
import data from 'emoji-mart-vue-fast/data/all.json';
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast';
import DateUtility from '../../utilities/dates';
import TextAreaInput from './TextAreaInput.vue';

export default {
  components: {
    RightSidePanel,
    Picker,
    TextAreaInput,
  },
  props: ['record'],
  data() {
    return {
      contactMessages: [],
      message: '',
      showEmoticons: false,
      index: new EmojiIndex(data),
      selectedTemplateId: '',
      smsTemplates: [],
      mergeTags: [
        '{{contact.name}}',
        '{{contact.first_name}}',
        '{{contact.last_name}}',
        '{{contact.email}}',
        '{{contact.phone_number}}',
        '{{contact.source}}',
        '{{contact.referral_link}}',
        '{{team.company}}',
      ],
      selectedTab: 'Merge Tags',
    };
  },
  mounted() {
    this.getContactMessages();
    this.getCableContactMessages();
    this.getSmsTemplates();
  },
  unmounted() {
    this.$cable.unsubscribe({
      channel: 'ContactMessagesChannel',
    });
  },
  channels: {
    ContactMessagesChannel: {
      connected() {
        console.log('connected to ContactMessagesChannel');
      },
      received(data) {
        if (data.new_message) {
          if (this.record.id == data.new_message.contact_id) {
            let originalContactMessages = this.contactMessages;
            originalContactMessages.push(data.new_message);
            this.contactMessages = originalContactMessages;
            this.$cable.perform({
              channel: 'ContactMessagesChannel',
              action: 'send_message',
              data: {
                contact_id: this.record.id,
              },
            });
            this.$nextTick(() => {
              document.getElementById('right-slide-panel').scrollTop = 100000;
            });
          }
        }
      },
    },
  },
  methods: {
    ...DateUtility,
    handleClosePanel() {
      this.$emit('handleContactChatCancel');
    },
    getContactMessages() {
      const self = this;
      self.$nextTick(() => {
        document.getElementById('right-slide-panel').scrollTop = 100000;
      });
      fetch(`/api/contacts/${self.record.id}/contact_messages`)
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.contactMessages = data.records;
          self.$nextTick(() => {
            document.getElementById('right-slide-panel').scrollTop = 100000;
          });
        });
    },
    sendMessage() {
      const self = this;
      let messageBody = self.message;
      self.message = '';
      fetch(`/api/contacts/${self.record.id}/send_message`, {
        method: 'POST',
        body: JSON.stringify({
          content: messageBody,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => response.json())
        .then(data => {
          self.$cable.perform({
            channel: 'ContactMessagesChannel',
            action: 'send_message',
            data: {
              contact_id: self.record.id,
            },
          });
          self.getContactMessages();
          if (data.status == 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Contact message error',
              alertDescription: data.message,
            });
          }
        });
    },
    getCableContactMessages() {
      const self = this;
      self.$cable.subscribe({
        channel: 'ContactMessagesChannel',
        id: self.record.id,
      });
    },
    getSmsTemplates() {
      const self = this;
      fetch(`/api/sms_templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => response.json())
        .then(data => {
          self.smsTemplates = data.records;
        });
    },
    setSmsTemplate(templateName) {
      const self = this;
      const selectedTemplate = self.smsTemplates.find(t => t.name === templateName);
      self.message = selectedTemplate.body;
    },
  },
};
</script>
